import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { WA_GENERAL } from "@/data/products";
import { scrollToHash } from "@/components/site/Navbar";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src="/assets/products/u-hero.jpg" alt="Planta de acero ANGORA" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#04070D] via-[#04070D]/55 to-[#04070D]/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#04070D]/80 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#FF7A00]" />
          <span className="font-mono-ind text-[11px] md:text-xs tracking-[0.25em] uppercase text-[#8B95A5]">
            Fabricantes de acero — Los Mochis, Sin. · Desde 2017
          </span>
        </div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-[clamp(2.5rem,6.8vw,6rem)]" style={{ fontWeight: 900 }}>
          <MaskedLine delay={0.15}>Acero.</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-outline">Construcción.</span></MaskedLine>
          <MaskedLine delay={0.45}><span className="text-[#FF7A00]">Ferretería.</span></MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-6 max-w-xl text-sm md:text-lg text-[#8B95A5] leading-relaxed"
        >
          Fabricamos y distribuimos lámina, perfil, tubo y viga de acero.
          Sin intermediarios: del rollo a tu obra, con corte a medida y envíos a todo México.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <a href={WA_GENERAL} target="_blank" rel="noopener noreferrer" data-testid="hero-quote-button"
            className="inline-flex items-center justify-center gap-2 bg-[#FF7A00] hover:bg-[#E06A00] text-white font-semibold px-8 py-4 rounded-sm transition-colors duration-200 text-sm md:text-base">
            <MessageCircle size={18} /> Cotizar por WhatsApp
          </a>
          <a href="#materiales" onClick={(e) => { e.preventDefault(); scrollToHash("#materiales"); }} data-testid="hero-catalog-button"
            className="inline-flex items-center justify-center gap-2 border border-[#1A2235] hover:border-[#FF7A00] hover:text-[#FF7A00] text-white font-semibold px-8 py-4 rounded-sm transition-colors duration-200 text-sm md:text-base bg-[#04070D]/40 backdrop-blur">
            Ver materiales <ArrowDown size={18} />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-12 grid grid-cols-3 max-w-2xl border-t border-[#1A2235] pt-6 gap-4"
        >
          {[
            ["2017", "Fabricando desde"],
            ["+3,000", "Entregas a obra"],
            ["100%", "Fabricación propia"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-2xl md:text-4xl font-bold tracking-tight">{n}</div>
              <div className="text-[11px] md:text-xs text-[#8B95A5] uppercase tracking-widest mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
